import { useMemo } from 'react';
import { useAuth } from 'react-oidc-context';

import { config } from '../../../config/config';
import { getClient } from '../../main/graphql';
import { usePromiseValue } from '../../shared/hooks/use-promise-value';
import type {
    IGetEnhancedGeolocationProviderQuery,
    IGetEnhancedGeolocationProviderQueryVariables,
} from '../graphql/geolocation-provider.graphql';
import {
    IGeolocationProvider,
    GetEnhancedGeolocationProviderDocument,
} from '../graphql/geolocation-provider.graphql';

export function useIsEnhancedGeolocationProviderEnabled() {
    const auth = useAuth();
    const profileId = auth.user?.profile?.user_profile_id as string;

    // This will be called outside of graphql context, therefore we need to manually
    // make the query without using a hook as theres no client context.
    const geolocation = useMemo(() => {
        if (!profileId) {
            return;
        }

        return getClient()
            .query<
                IGetEnhancedGeolocationProviderQuery,
                IGetEnhancedGeolocationProviderQueryVariables
            >(GetEnhancedGeolocationProviderDocument, {
                xTenantID: config.TENANT_ID,
                userProfileId: profileId,
                skinId: config.SKIN_ID,
            })
            .toPromise();
    }, [profileId]);
    const response = usePromiseValue(geolocation);

    if (response.resolving) {
        // If we're loading we might as well load up the geolocation library already.
        // we can always stop it if we don't need it.
        return true;
    }

    return (
        response.data?.data?.getEnhancedGeolocationProvider.provider !== IGeolocationProvider.None
    );
}
